import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useMatch, useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { SortOrder, Modules, AuthRoutes, RouteParamsTypes } from '../../../../enums';
import { VALIDATION_FIELD_MAPPING, APP_SUCCESS_MESSAGES, DEFAULTS, GENERIC_LOOKUP_KEYS, MaxLength } from '../../../../utils/appConstant';
import { AddEdit as AddEditInterface, AddEditApi } from '../../../../interfaces/property.interface';
import { HeadCellType, TableContextProvider, Validation } from '../../../../interfaces/common';
import useFetch from '../../../../hooks/useFetch';
import useSorting from '../../../../hooks/useSorting';
import usePagination from '../../../../hooks/usePagination';
import PropertyService from '../../../../services/property.service';
import CommonService from '../../../../services/common.service';
import UserService from '../../../../services/user.service';
import CustomerService from '../../../../services/customer.service';
import PlatformService from '../../../../services/platform.service';
import Header from './components/Header';
import Buttons from './components/Buttons';
import PropertyDetailsTab from './components/PropertyDetailsTab';
import Tabs from "./components/Tabs";
import UsersTab from './components/UsersTab';
import Row from './components/Row';
import Button from "../../../../components/Button";
import Modal from '../../../../components/Modal';
import useUnsavedChangesWarning from '../../../../hooks/useUnsavedChangesWarning';
import ConfirmationFormRouting from '../../../../components/ConfirmationFormRouting';
import { validateAddEditForm } from './validations';
import { buildHeadCells } from '../../../../utils/methods';
import Spinner from '../../../../components/Spinner';
import Toast from '../../../../components/Toast';
import { STATUS_CODE } from '../../../../utils/apiConstant';

const initialFormData: AddEditInterface = {
  customer: '',
  hospitableId: '',
  managedBy: '',
  cleaningManagedBy: '',
  name: '',
  publicName: '',
  pictureUrl: '',
  address1: '',
  address2: '',
  city: '',
  state: DEFAULTS.state,
  postal: '',
  country: DEFAULTS.country,
  commissionPercentage: '',
  configureMatTax: false,
  matTaxPercentage: '',
  notes: '',
  status: true,
  bookingType: '',
  user: '',
  propertyWhoCollects: []
}

const initialValidation: Validation = { validate: true, fields: [] };

const initialDropdownSearchData = {
  customer: '',
  managedBy: '',
  cleaningManagedBy: '',
  bookingType: '',
  country: '',
  state: '',
  user: '',
  city: ''
}

const AddEditView: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { id } = useParams();
  const view = !!useMatch(Modules.resources + AuthRoutes.properties + RouteParamsTypes.view + '/:id');
  const edit = !!useMatch(Modules.resources + AuthRoutes.properties + RouteParamsTypes.edit + '/:id');
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [tab, setTab] = useState<'propertyDetails' | 'users'>('propertyDetails');
  const [formData, setFormData] = useState<AddEditInterface>(initialFormData);
  const [dropdownSearchData, setDropdownSearchData] = useState<any>(initialDropdownSearchData);
  const [loading, setLoading] = useState(false);
  const [assignmentLoading, setAssignmentLoading] = useState<boolean>(false);
  const [actionTwoLoading, setActionTwoLoading] = useState<boolean>(false);
  const [counter, setCounter] = useState<number>(0);
  const [validation, setValidation] = useState<Validation>(initialValidation);
  const [removalId, setRemovalId] = useState<string>('');
  const [showMatConfirm, setShowMatConfirm] = useState(false);
  const [pendingSubmitAction, setPendingSubmitAction] = useState<'1' | '2'>('1');
  const currentIdRef = useRef<string | undefined>(id);

  const [shouldBlock, setShouldBlock] = useState(!view);

  // Unsaved changes warning
  const { showPrompt, confirmNavigation, cancelNavigation } = useUnsavedChangesWarning({
    when: shouldBlock,
  });

  useEffect(() => {
    setTab(location?.state?.tab ?? 'propertyDetails');
  }, [location.state, location.key]);

  const headCells: HeadCellType[] = buildHeadCells([
    VALIDATION_FIELD_MAPPING.actions,
    VALIDATION_FIELD_MAPPING.name,
    VALIDATION_FIELD_MAPPING.whoCollectsMoney,
    VALIDATION_FIELD_MAPPING.whoCollectsHst,
    VALIDATION_FIELD_MAPPING.matManagedBy,
  ], 'platformListInPropertyForm');

  const {
    order, orderBy, setOrder, setOrderBy,
  } = useSorting({ defaultOrder: SortOrder.asc, defaultOrderBy: VALIDATION_FIELD_MAPPING.name });
  const {
    page, perPage, setPage, setPerPage,
  } = usePagination({ defaultPage: Number(searchParams.get('page')) || 1 });

  const { data: platforms, loading: platformsLoading } = useFetch({
    service: PlatformService,
    func: 'lookup',
    payload: { searchText: '' },
    dependency: [location.key],
    onSuccess: (response) => {
      setFormData((prev: AddEditInterface) => {
        return {
          ...prev,
          propertyWhoCollects: (response?.records || []).map((item: any) => {
            return {
              platformId: item.id,
              checked: false,
              whoCollectsMoney: '',
              whoCollectsHst: '',
              matManagedBy: '',
            }
          })
        }
      })
      setDropdownSearchData((prev: any) => {
        return {
          ...prev,
          propertyWhoCollects: (response?.records || []).map((item: any) => {
            return {
              platformId: item.id,
              whoCollectsMoney: '',
              whoCollectsHst: '',
            }
          })
        }
      })
    }
  });

  const { data: users } = useFetch({
    service: PropertyService,
    func: 'propertyUsers',
    payload: {
      id: id,
      page: 1,
      perPage: MaxLength.pageSize,
      is_active: '1'
    },
    dependency: [id, counter],
    skip: !id
  });

  const { data: allUsersLookup } = useFetch({
    service: UserService,
    func: 'lookup',
    payload: {
      searchText: '',
      exclude: 'properties',
      exclude_id: id,
      is_active: '1'
    },
    dependency: [id, counter],
    skip: !id
  });

  const { data: usersLookup } = useFetch({
    service: UserService,
    func: 'lookup',
    payload: {
      searchText: dropdownSearchData.user,
      exclude: 'properties',
      exclude_id: id,
      is_active: '1'
    },
    dependency: [id, counter, dropdownSearchData.user],
    skip: !id
  });

  const { data: allCountries } = useFetch({
    service: CommonService,
    func: 'countriesLookup',
    payload: { searchText: '' },
    dependency: [],
  });

  const { data: allStates } = useFetch({
    service: CommonService,
    func: 'statesLookup',
    payload: { searchText: '' },
    dependency: []
  });

  const { data: countries } = useFetch({
    service: CommonService,
    func: 'countriesLookup',
    payload: { searchText: dropdownSearchData.country },
    dependency: [dropdownSearchData.country]
  });

  const { data: states } = useFetch({
    service: CommonService,
    func: 'statesLookup',
    payload: { searchText: dropdownSearchData.state },
    dependency: [dropdownSearchData.state]
  });

  const { data: allCities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: { searchText: '', stateId: formData.state },
    dependency: [formData.state]
  });

  const { data: cities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: { searchText: dropdownSearchData.city, stateId: formData.state },
    dependency: [dropdownSearchData.city, formData.state]
  });

  const { data: allManagers } = useFetch({
    service: UserService,
    func: 'managersLookup',
    payload: { searchText: '', ...(!edit && !view && { is_active: '1' }) },
    dependency: []
  });

  const { data: managers } = useFetch({
    service: UserService,
    func: 'managersLookup',
    payload: { searchText: dropdownSearchData.managedBy, ...(!edit && !view && { is_active: '1' }) },
    dependency: [dropdownSearchData.managedBy]
  });

  const { data: allCustomers } = useFetch({
    service: CustomerService,
    func: 'lookup',
    payload: { searchText: '', ...(!edit && !view && { is_active: '1' }) },
    dependency: []
  });

  const { data: customers } = useFetch({
    service: CustomerService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.customer, ...(!edit && !view && { is_active: '1' }) },
    dependency: [dropdownSearchData.customer]
  });

  const { data: allBookingTypes } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.propertyBookingType, searchText: '' },
    dependency: []
  });

  const { data: bookingTypes } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.propertyBookingType, searchText: dropdownSearchData.bookingType },
    dependency: [dropdownSearchData.bookingType]
  });

  const { data: allWhoCollectsMoneyOptions } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.whoCollectsMoney, searchText: '' },
    dependency: []
  });

  const { data: cleaningManagedByOptions } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.whoCollectsMoney, searchText: dropdownSearchData.cleaningManagedBy },
    dependency: [dropdownSearchData.cleaningManagedBy]
  });

  const { data: allWhoCollectsHstOptions } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.whoCollectsHst, searchText: '' },
    dependency: []
  });

  const { data: allWhoCollectsMatOptions } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.whoCollectsMat, searchText: '' },
    dependency: []
  });

  useEffect(() => {
    currentIdRef.current = id;
    setFormData(initialFormData)
    setDropdownSearchData(initialDropdownSearchData)
    setValidation(initialValidation)
    setShouldBlock(!view);
  }, [location.key, id, view]);

  const { loading: getContentLoading } = useFetch({
    service: PropertyService,
    func: 'get',
    payload: id,
    dependency: [id, location.key],
    skip: !id,
    onSuccess: async (response: any) => {
      if (!currentIdRef.current) return;

      const availablePlatformIds = response.property_who_collects.map((item: any) => item.platform_id)
      const platformLookupResposne: any = await PlatformService.lookup({ searchText: '' })

      if (!currentIdRef.current) return;

      setFormData({
        customer: response.customer_id,
        hospitableId: response.hospitable_id || '',
        managedBy: response.managed_by,
        name: response.name,
        publicName: response.public_name,
        pictureUrl: response.picture_url || '',
        address1: response.address1,
        address2: response.address2 || '',
        city: response.city,
        state: response.state,
        postal: response.postal,
        country: response.country,
        commissionPercentage: response?.commission_percentage ?? '',
        configureMatTax: !!response?.mat_tax,
        matTaxPercentage: response?.mat_tax_percentage != null ? String(response.mat_tax_percentage) : '',
        cleaningManagedBy: response.cleaning_managed_by || '',
        notes: response.notes || '',
        status: !!response.is_active,
        bookingType: response.property_booking_type,
        propertyWhoCollects: [
          ...(response.property_who_collects || []).map((item: any) => {
            return {
              platformId: item.platform_id,
              checked: true,
              whoCollectsMoney: item.who_collects_money,
              whoCollectsHst: item.who_collects_hst,
              matManagedBy: item.who_collects_mat || '',
            }
          }),
          ...platformLookupResposne.records.filter((platform: any) => !availablePlatformIds.includes(platform.id)).map((item: any) => {
            return {
              platformId: item.id,
              checked: false,
              whoCollectsMoney: '',
              whoCollectsHst: '',
              matManagedBy: '',
            }
          })
        ]
      });
    },
    onError: (error: any) => {
      if (error.code === STATUS_CODE.notFound) navigate(Modules.resources + AuthRoutes.properties);
    }
  });

  const handleTabChange = useCallback((e: any, value: 'propertyDetails' | 'users') => {
    setTab(value);
  }, []);

  const handleInputChange = useCallback((event: any, name: string, platformId?: string) => {
    if (
      name === VALIDATION_FIELD_MAPPING.actions ||
      name === VALIDATION_FIELD_MAPPING.whoCollectsHst ||
      name === VALIDATION_FIELD_MAPPING.whoCollectsMoney ||
      name === VALIDATION_FIELD_MAPPING.matManagedBy
    ) {
      let value: any;

      if (name === VALIDATION_FIELD_MAPPING.actions) {
        value = event.target.checked
      } else {
        value = event.target.value
      }

      setFormData((prev) => {
        const customName = (name === VALIDATION_FIELD_MAPPING.actions ? 'checked' : name)
        const newPropertyWhoCollects = prev.propertyWhoCollects.map(item => (item.platformId == platformId) ? { ...item, [customName]: value } : item)
        return {
          ...prev,
          propertyWhoCollects: newPropertyWhoCollects
        };
      });
    } else if (name === VALIDATION_FIELD_MAPPING.state) {
      setFormData((prev) => {
        return {
          ...prev,
          [VALIDATION_FIELD_MAPPING.city]: '',
          [name]: event.target.value
        };
      });
    } else {
      let value: any;

      if (name === VALIDATION_FIELD_MAPPING.status || name === VALIDATION_FIELD_MAPPING.configureMatTax) {
        value = event.target.checked
      } else {
        value = event.target.value
      }

      setFormData((prev) => {
        const updated: any = { ...prev, [name]: value };
        if (name === VALIDATION_FIELD_MAPPING.configureMatTax && !value) {
          updated.matTaxPercentage = '';
        }
        return updated;
      });

      if (name === VALIDATION_FIELD_MAPPING.configureMatTax && !event.target.checked) {
        setValidation((prev) => ({
          ...prev,
          fields: prev.fields.filter(f => f.field !== VALIDATION_FIELD_MAPPING.matTaxPercentage),
        }));
      }
    }
  }, [formData, setFormData]);

  const handleDropdownSearchInputChange = useCallback((event: any, name: string) => {
    setDropdownSearchData((prev: any) => {
      return {
        ...prev,
        [name]: event.target.value,
      };
    });
  }, [setDropdownSearchData]);

  const handleInputBlur = useCallback((event: any, name: string, platformId?: string) => {
    if (
      name === VALIDATION_FIELD_MAPPING.whoCollectsHst ||
      name === VALIDATION_FIELD_MAPPING.whoCollectsMoney ||
      name === VALIDATION_FIELD_MAPPING.matManagedBy
    ) {
      const newPropertyWhoCollects = formData.propertyWhoCollects.map(item => (item.platformId == platformId) ? { ...item, [name]: event.target.value.trim() } : item)

      const newValidation = validateAddEditForm({
        ...formData,
        propertyWhoCollects: newPropertyWhoCollects,
      }, name);
      setValidation(prev => {
        const filteredFields = prev.fields.filter(f => f.field !== name);
        const mergedFields = [...filteredFields, ...newValidation.fields];

        return {
          validate: mergedFields.length === 0,
          fields: mergedFields,
        };
      });
    } else {
      const newValidation = validateAddEditForm({
        ...formData,
        [name]: event.target.value.trim(),
      }, name);
      setValidation(prev => {
        const filteredFields = prev.fields.filter(f => f.field !== name);
        const mergedFields = [...filteredFields, ...newValidation.fields];

        return {
          validate: mergedFields.length === 0,
          fields: mergedFields,
        };
      });
    }
  }, [formData]);

  const buildPayload = useCallback((): AddEditApi => {
    return {
      customer_id: formData.customer,
      hospitable_id: formData.hospitableId?.trim(),
      managed_by: formData.managedBy,
      name: formData.name.trim(),
      public_name: formData.publicName.trim(),
      picture_url: formData.pictureUrl?.trim(),
      address1: formData.address1.trim(),
      address2: formData.address2?.trim(),
      city: formData.city,
      state: formData.state,
      postal: formData.postal.trim(),
      country: formData.country,
      cleaning_managed_by: formData.cleaningManagedBy?.trim(),
      commission_percentage: Number(formData.commissionPercentage),
      mat_tax: formData.configureMatTax,
      mat_tax_percentage: formData.configureMatTax ? Number(formData.matTaxPercentage) : null,
      notes: formData.notes?.trim() || '',
      is_active: formData.status,
      property_booking_type: formData.bookingType,
      property_who_collects: (formData.propertyWhoCollects || []).filter(item => item.checked).map(item => {
        return {
          platform_id: item.platformId,
          who_collects_money: item.whoCollectsMoney,
          who_collects_hst: item.whoCollectsHst,
          who_collects_mat: item.matManagedBy,
          default_cleaning_charge: "0"
        }
      })
    };
  }, [formData]);

  const executeSubmit = useCallback(async (action: '1' | '2') => {
    if (action === '1') setLoading(true);
    else setActionTwoLoading(true);

    try {
      const payload = buildPayload();

      let response: any;
      if (!edit) {
        response = await PropertyService.add(payload);
      } else if (id) {
        response = await PropertyService.edit(id, payload);
      }

      if (response?.success !== false) {
        toast.success(<Toast message={id ? APP_SUCCESS_MESSAGES.propertyUpdated : APP_SUCCESS_MESSAGES.propertyCreated} />, { icon: false });

        setShouldBlock(false);
        setTimeout(() => {
          if (edit) {
            navigate(Modules.resources + AuthRoutes.properties);
          } else if (action === '2') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            const selectElement = document.querySelector(`[data-testid="select-${VALIDATION_FIELD_MAPPING.customer}"]`) as HTMLElement;
            selectElement && selectElement.click();
            navigate(Modules.resources + AuthRoutes.properties + RouteParamsTypes.add, { replace: true });
          } else {
            const createdId = response?.id;
            navigate(`${Modules.resources}${AuthRoutes.properties}${RouteParamsTypes.edit}/${createdId}`, { state: { tab: 'users' }, replace: true });
          }
        }, 0);
      }

      if (action === '1') setLoading(false);
      else setActionTwoLoading(false);
    } catch (err) {
      if (action === '1') setLoading(false);
      else setActionTwoLoading(false);
    }
  }, [id, edit, buildPayload, navigate]);

  const handleSubmit = useCallback((action: '1' | '2', event?: React.FormEvent) => {
    event && event.preventDefault();

    const newValidation = validateAddEditForm(formData);
    setValidation(newValidation);
    if (!newValidation.validate) return;

    const hasCustomerMatManagedBy = formData.propertyWhoCollects.some(
      item => item.checked && item.matManagedBy === 'customer'
    );

    if (hasCustomerMatManagedBy) {
      setPendingSubmitAction(action);
      setShowMatConfirm(true);
    } else {
      executeSubmit(action);
    }
  }, [formData, executeSubmit]);

  const handleKeyDown = useCallback((e: any, name?: string) => {
    if (name === VALIDATION_FIELD_MAPPING.notes) return;
    if (e.key === 'Enter') handleSubmit('1');
  }, [handleSubmit]);

  const handleCancel = () => {
    setShouldBlock(false);
    setTimeout(() => {
      navigate(Modules.resources + AuthRoutes.properties);
    }, 0);
  }

  const handleAssignUser = useCallback(async (userId: string) => {
    setAssignmentLoading(true);

    try {
      await PropertyService.assignUser(id || '', userId);

      setAssignmentLoading(false);
      setCounter((prev) => prev + 1);
      setDropdownSearchData({ ...dropdownSearchData, user: '' });
      setFormData({ ...formData, user: '' });
      toast.success(<Toast message={APP_SUCCESS_MESSAGES.userAssigned} />, { icon: false });
    } catch (e) {
      setAssignmentLoading(false);
    }
  }, [dropdownSearchData, id, formData]);

  const handleRemoveUser = useCallback(async (assignmentId: string) => {
    try {
      await PropertyService.removeUser(assignmentId);
      setCounter((prev) => prev + 1);
      setRemovalId('');
    } catch (e) {
    }
  }, []);

  const handleRemoveUserClick = useCallback((assignmentId: string) => {
    setRemovalId(assignmentId)
  }, [])

  const modalAction = (
    <>
      <Button
        className="btn btn-outline-secondary me-3"
        text={t("property.addEdit.pageTexts.form.confirmModal.cancelBtn")}
        onClick={_e => setRemovalId('')}
      />
      <Button
        className="btn btn-danger me-3"
        text={t("property.addEdit.pageTexts.form.confirmModal.removeBtn")}
        onClick={_e => handleRemoveUser(removalId)}
      />
    </>
  )

  const matConfirmAction = (
    <div className="d-flex gap-3">
      <Button
        className="btn btn-outline-secondary"
        text={t("property.addEdit.pageTexts.form.matConfirmModal.noBtn")}
        onClick={() => setShowMatConfirm(false)}
      />
      <Button
        className="btn btn-primary"
        text={t("property.addEdit.pageTexts.form.matConfirmModal.yesBtn")}
        onClick={() => {
          setShowMatConfirm(false);
          executeSubmit(pendingSubmitAction);
        }}
      />
    </div>
  )

  const values: TableContextProvider = useMemo(() => {
    return {
      rowProps: {
        allWhoCollectsHstOptions, allWhoCollectsMoneyOptions, allMatManagedByOptions: allWhoCollectsMatOptions, view, formData, validation,
        onInputBlur: handleInputBlur, onInputChange: handleInputChange
      },
      pagination: false, row: Row, data: platforms || [], headCells, order, orderBy, page, perPage, total: platforms.length, loading: platformsLoading, setPage, setPerPage, setOrder, setOrderBy
    };
  }, [
    allWhoCollectsHstOptions, allWhoCollectsMoneyOptions, allWhoCollectsMatOptions, view, formData, validation,
    handleKeyDown, handleInputBlur, handleInputChange,
    headCells, order, orderBy, page, perPage, platforms, platformsLoading, setPage, setPerPage, setOrder, setOrderBy
  ]);

  let formContent;

  if (getContentLoading) {
    formContent = (
      <div className='card-header text-center'>
        <Spinner />
      </div>
    )
  } else if (tab === 'propertyDetails') {
    formContent = (
      <PropertyDetailsTab
        formData={formData}
        validation={validation}
        values={values}
        view={view}
        managers={managers}
        allManagers={allManagers}
        customers={customers}
        allCustomers={allCustomers}
        countries={countries}
        states={states}
        allCountries={allCountries}
        allStates={allStates}
        cities={cities}
        allCities={allCities}
        bookingTypes={bookingTypes}
        allBookingTypes={allBookingTypes}
        cleaningManagedByOptions={cleaningManagedByOptions}
        allWhoCollectsMoneyOptions={allWhoCollectsMoneyOptions}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        onKeyDown={handleKeyDown}
        onInputBlur={handleInputBlur}
        onInputChange={handleInputChange}
      />
    )
  } else if (tab === 'users') {
    formContent = (
      <UsersTab
        options={usersLookup}
        allOptions={allUsersLookup}
        dropdownSearchData={dropdownSearchData}
        formData={formData}
        loading={assignmentLoading}
        users={users}
        view={view}
        edit={edit}
        onAssignUser={handleAssignUser}
        onRemoveUser={handleRemoveUserClick}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        onInputChange={handleInputChange}
      />
    )
  }

  return (
    <>
      <Header id={id} view={view} edit={edit} loading={getContentLoading} />

      <div className="card mb-6">
        <h5 className="card-header">{!id ? t('property.addEdit.pageTexts.title.addTitle') : (view ? t('property.addEdit.pageTexts.title.viewTitle') : t('property.addEdit.pageTexts.title.editTitle'))}</h5>

        <hr className="m-0" />

        <Tabs tab={tab} add={!edit && !view} onTabChange={handleTabChange} />

        <hr className="m-0"></hr>

        {formContent}

        <hr className="m-0" />

        <Buttons
          tab={tab}
          actionTwoLoading={actionTwoLoading}
          loading={loading}
          view={view}
          edit={edit}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </div>

      <ConfirmationFormRouting
        open={showPrompt}
        onClose={cancelNavigation}
        onConfirm={confirmNavigation}
      />

      <Modal
        open={removalId ? true : false}
        onClose={() => setRemovalId('')}
        title={t("property.addEdit.pageTexts.form.confirmModal.title")}
        content={t("property.addEdit.pageTexts.form.confirmModal.content")}
        action={modalAction}
        contentClassName="text-center"
        maxWidth="sm"
      />

      <Modal
        open={showMatConfirm}
        onClose={() => setShowMatConfirm(false)}
        title={t("property.addEdit.pageTexts.form.matConfirmModal.title")}
        content={t("property.addEdit.pageTexts.form.matConfirmModal.content")}
        action={matConfirmAction}
        contentClassName="text-center"
        maxWidth="sm"
      />
    </>
  );
};

export default AddEditView;
